import { Document, Packer, Paragraph, TextRun } from 'docx';

export const generateLetterDocx = (schoolName: string) => {
  const doc = new Document({
    sections: [
      {
        properties: {
          page: {
            margin: {
              top: 1440, // 1 inch
              right: 1440, // 1 inch
              bottom: 1440, // 1 inch
              left: 1440, // 1 inch
            },
          },
        },
        children: [
          // Date placeholder
          new Paragraph({
            children: [
              new TextRun({
                text: '[Date]',
                size: 24, // 12pt
              }),
            ],
            spacing: { after: 240 }, // adds space after paragraph
          }),

          // Greeting
          new Paragraph({
            children: [
              new TextRun({
                text: 'Dear Parent/Carer,',
                size: 24, // 12pt
              }),
            ],
            spacing: { after: 240 }, // adds space after paragraph
          }),

          // Introduction paragraph
          new Paragraph({
            children: [
              new TextRun({
                text: 'As part of our literacy instruction at ',
                size: 24, // 12pt
              }),
              new TextRun({
                text: schoolName,
                size: 24, // 12pt
              }),
              new TextRun({
                text: ', we are pleased to introduce ',
                size: 24, // 12pt
              }),
              new TextRun({
                text: 'Spellcaster',
                size: 24, // 12pt
                bold: true,
              }),
              new TextRun({
                text: ' to our classroom resources. Spellcaster is an educational platform designed to support all students with their spelling and reading skills through engaging and structured practice.',
                size: 24, // 12pt
              }),
            ],
            spacing: { after: 240 }, // adds space after paragraph
          }),

          // Educational approach paragraph
          new Paragraph({
            children: [
              new TextRun({
                text: 'Spellcaster',
                size: 24, // 12pt
                bold: true,
              }),
              new TextRun({
                text: ' is currently being used in schools, speech therapy clinics and literacy intervention settings across Australia and New Zealand. The program is designed to align with systematic synthetic phonics instruction - widely recognised as the gold standard for effective teaching of reading and spelling. The program combines adventure gameplay with evidence-based approaches to literacy development, including important elements of morphology and phonics.',
                size: 24, // 12pt
              }),
            ],
            spacing: { after: 240 }, // adds space after paragraph
          }),

          // Game structure paragraph
          new Paragraph({
            children: [
              new TextRun({
                text: 'Students will progress through a fantasy realm where they complete spelling challenges that systematically introduce and reinforce specific patterns. Each level requires 90% accuracy to advance, ensuring students build a solid foundation before moving to more complex concepts. The program includes multiple minigames that target different aspects of spelling and reading:',
                size: 24, // 12pt
              }),
            ],
            spacing: { after: 240 }, // adds space after paragraph
          }),

          // Minigames bullet points
          new Paragraph({
            children: [
              new TextRun({
                text: 'Mystic Match: Focuses on matching sounds with their corresponding letters',
                size: 24, // 12pt
              }),
            ],
            bullet: { level: 0 },
            spacing: { after: 120 }, // space between bullet points
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: 'Phoneme Forge: Develops ability to manipulate individual sounds within words',
                size: 24, // 12pt
              }),
            ],
            bullet: { level: 0 },
            spacing: { after: 120 }, // space between bullet points
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: 'Word Weaver: Challenges students to construct complete words from component sounds',
                size: 24, // 12pt
              }),
            ],
            bullet: { level: 0 },
            spacing: { after: 120 }, // space between bullet points
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: 'Word Sums: Building words from morphemes like bases, suffixes and prefixes',
                size: 24, // 12pt
              }),
            ],
            bullet: { level: 0 },
            spacing: { after: 120 }, // space between bullet points
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: "Tricky Words: Addresses high-frequency words that don't follow regular patterns",
                size: 24, // 12pt
              }),
            ],
            bullet: { level: 0 },
            spacing: { after: 120 }, // space between bullet points
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: 'Sentence Builder: Applies skills at the sentence level',
                size: 24, // 12pt
              }),
            ],
            bullet: { level: 0 },
            spacing: { after: 120 }, // space between bullet points
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: 'Word Rifts: A simple comprehension activity where students fill in the blank',
                size: 24, // 12pt
              }),
            ],
            bullet: { level: 0 },
            spacing: { after: 120 }, // space between bullet points
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: 'Morph Master: A word chaining activity where learners swap morphemes to make new words or change the meaning of a word',
                size: 24, // 12pt
              }),
            ],
            bullet: { level: 0 },
            spacing: { after: 240 }, // more space after last bullet point
          }),

          // Program review paragraph
          new Paragraph({
            children: [
              new TextRun({
                text: 'The program constantly reviews previously learned concepts to enhance retention, while our teaching staff will have access to detailed progress reports to guide instruction.',
                size: 24, // 12pt
              }),
            ],
            spacing: { after: 240 }, // adds space after paragraph
          }),

          // Home support header
          new Paragraph({
            children: [
              new TextRun({
                text: 'How Families Can Support at Home:',
                size: 24, // 12pt
                bold: true,
              }),
            ],
            spacing: { after: 120 }, // less space after heading
          }),

          // Home support paragraph
          new Paragraph({
            children: [
              new TextRun({
                text: "We encourage you to support your child's learning journey with Spellcaster. Here are some ways you can help:",
                size: 24, // 12pt
              }),
            ],
            spacing: { after: 120 }, // space after intro to bullet points
          }),

          // Home support bullet points
          new Paragraph({
            children: [
              new TextRun({
                text: 'Sit with your child while they use Spellcaster',
                size: 24, // 12pt
              }),
            ],
            bullet: { level: 0 },
            spacing: { after: 120 }, // space between bullet points
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: 'Ask them to think about and sound out the words they are spelling with language like "What letter(s) spell the /sh/ sound in "ship" or the /m/ sound in "mat"',
                size: 24, // 12pt
              }),
            ],
            bullet: { level: 0 },
            spacing: { after: 120 }, // space between bullet points
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: 'Prompt them with questions like "What\'s the first sound you hear in the word \'cat\'?" or "Which letter(s) can we use to spell that sound?"',
                size: 24, // 12pt
              }),
            ],
            bullet: { level: 0 },
            spacing: { after: 120 }, // space between bullet points
          }),
          new Paragraph({
            children: [
              new TextRun({
                text: 'If they make an error, provide guidance by saying "That letter/those letters spell the [insert sound]"',
                size: 24, // 12pt
              }),
            ],
            bullet: { level: 0 },
            spacing: { after: 240 }, // more space after last bullet point
          }),

          // Closing paragraph
          new Paragraph({
            children: [
              new TextRun({
                text: 'At ',
                size: 24, // 12pt
              }),
              new TextRun({
                text: schoolName,
                size: 24, // 12pt
              }),
              new TextRun({
                text: ", we are excited about incorporating Spellcaster into our educational resources and the benefits it will bring to our students' literacy development.",
                size: 24, // 12pt
              }),
            ],
            spacing: { after: 240 }, // adds space after paragraph
          }),

          // Signature
          new Paragraph({
            children: [
              new TextRun({
                text: 'Yours sincerely,',
                size: 24, // 12pt
              }),
            ],
            spacing: { after: 240 }, // adds space after paragraph
          }),

          // Teacher placeholder
          new Paragraph({
            children: [
              new TextRun({
                text: '[Teacher Name] [Position]',
                size: 24, // 12pt
              }),
            ],
          }),
        ],
      },
    ],
    styles: {
      default: {
        document: {
          run: {
            font: 'Times New Roman',
            size: 24,
          },
        },
      },
    },
  });

  return doc;
};

export const downloadDocxFile = async (doc: Document, fileName: string) => {
  const blob = await Packer.toBlob(doc);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
